import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { AdvertSnippetBlock } from "../../blocks/AdvertSnippetBlock";
import { HomeAdvert } from "../../blocks/HomeAdvert";
import { HomeBannerAd } from "../../blocks/HomeBannerAd";
import { HomeHeroBanner } from "../../blocks/HomeHeroBanner";
import { ProductList } from "../../blocks/ProductList";
import { StoreLocator } from "../../blocks/StoreLocator";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * sealy HomePage body assembly (#33585): dispatches every
 * ``HomePageBodyContent`` block (the 30 Toolbox keys + 6 legacy home blocks)
 * in one React render tree, collapsing the legacy per-block
 * ``{% include_block %}`` hydration roots into a single island.
 *
 * FAITHFUL port of the legacy ``cms/home_page.html`` body render (verified at
 * git dev): a bare ``{% for block in self.body %}{% include_block block %}{% endfor %}``
 * loop -- NO whole-field render, therefore NO Wagtail
 * ``w-block-*``/``block-*`` wrapper per block. Reproduced with
 * ``tagName={React.Fragment}`` (toppers HomePage precedent; contrast the
 * sealy Generic/Basic assemblies, whose legacy templates DID render the whole
 * field and therefore wrap).
 *
 * All 6 legacy keys are now real ported components -- no
 * legacy pre-rendered HTML strings remain on this page. The
 * ``advert`` / ``store_locator`` / ``banner_ad`` StaticBlocks were ported in
 * #33285 batch A; ``home_hero_banner`` in batch B; ``product_list`` /
 * ``advert_snippet`` in batch C.
 */
export const homePageComponentsMap = {
    ...toolboxComponentsMap,
    home_hero_banner: HomeHeroBanner,
    product_list: ProductList,
    advert: HomeAdvert,
    store_locator: StoreLocator,
    advert_snippet: AdvertSnippetBlock,
    banner_ad: HomeBannerAd,
} as const;

export const HomePageBody = (
    props: Immutable<{ page: templates.HomePageTemplate["page"] }>,
): React.JSX.Element => {
    // `body` is non-null (the model field is `blank=True` without
    // `null=True`); an empty stream renders nothing.
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={homePageComponentsMap}
        />
    );
};
